import { useState, useCallback, useEffect } from 'react';
import { SWATCHES } from './constants';
import { isLight } from './utils';
import classNames from 'classnames';

interface ColorSwatchPickerProps {
  title?: string | React.ReactNode | undefined;
  value?: string;
  onChange?: (color: string) => void;
  containerClassName?: string;
}

export default function ColorSwatchPicker({
  title,
  value = '#C0C0C0',
  onChange,
  containerClassName,
}: ColorSwatchPickerProps) {
  const [selectedIndex, setSelectedIndex] = useState<number>(() =>
    SWATCHES.findIndex((c) => c.toUpperCase() === value.toUpperCase())
  );
  const [hex, setHex] = useState<string>(value.replace('#', '').toUpperCase());

  const handleSwatchClick = useCallback(
    (color: string, index: number) => {
      const upper = color.toUpperCase();
      setSelectedIndex(index);
      setHex(upper.replace('#', ''));
      onChange?.(color);
    },
    [onChange]
  );

  const handleHexInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
      .replace(/[^0-9a-fA-F]/g, '')
      .toUpperCase()
      .slice(0, 6);
    setHex(raw);
    if (raw.length === 6) {
      const full = `#${raw}`;
      setSelectedIndex(
        SWATCHES.findIndex((c) => c.toUpperCase() === full.toUpperCase())
      );
      onChange?.(full);
    }
  };

  const currentColor = `#${hex.padEnd(6, '0')}`;

  useEffect(() => {
    setSelectedIndex(
      SWATCHES.findIndex((c) => c.toUpperCase() === value.toUpperCase())
    );
    setHex(value.replace('#', '').toUpperCase());
  }, [value]);

  return (
    <div className={classNames('color-swatch-picker', containerClassName)}>
      {title && <h2 className='color-swatch-picker__title'>{title}</h2>}

      <div className='color-swatch-picker__grid'>
        {SWATCHES.map((color, index) => {
          const isSelected = selectedIndex === index;
          return (
            <button
              key={`${index}-${color}`}
              type='button'
              onClick={() => handleSwatchClick(color, index)}
              title={`${color} (${index})`}
              className={`color-swatch-picker__swatch ${isSelected ? 'color-swatch-picker__swatch--selected' : ''}`}
              style={{ background: color }}
            >
              {isSelected && (
                <span
                  className='color-swatch-picker__swatch-check'
                  style={{
                    color: isLight(color) ? '#000' : '#fff',
                  }}
                >
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className='color-swatch-picker__hex-row mt-3'>
        <span className='color-swatch-picker__hex-label'>HEX</span>
        <div
          className='color-swatch-picker__hex-preview'
          style={{ background: currentColor }}
        />
        <input
          type='text'
          className='color-swatch-picker__hex-input'
          value={hex}
          onChange={handleHexInput}
          maxLength={6}
        />
      </div>
    </div>
  );
}
